import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { useFormValues } from '@s20/components/forms/FormContext';
import { useInstanceSelector } from '@s20/hooks/redux.hooks';
import { checkUserHasPermission } from '@s20/selectors/features/permissions.selectors';
import { ApiRoutes, ApplicationPermissions } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SlideForm from '../common/forms/SlideForm';
import { toasty } from '../common/forms/FormElements';

const ForemanSelect = ({ unavailableForemanIds }) => {
    const companyId = useFormValues('companyId');

    const { data: foremen, isLoading } = useQuery({
        queryKey: ['companyForemen', companyId],
        queryFn: () => httpClient.js(ApiRoutes.company.foremen(companyId)),
        enabled: Boolean(companyId),
    });

    // Foremen already on the employee's list aren't offered again.
    // (Clearing the selection on company change is handled by the company select's onChange.)
    const availableForemen = (foremen ?? []).filter((f) => !unavailableForemanIds.includes(f.id));

    return (
        <ValidatedSelect
            id="foremanId"
            name="foremanId"
            label="Foreman"
            required
            options={availableForemen}
            valueField="id"
            isLoading={isLoading}
            isDisabled={!companyId}
            getOptionLabel={(option) => option.fullName}
            getOptionValue={(option) => option.id}
            error="A foreman selection is required."
        />
    );
};

export const EmployeePreferredForemanForm = ({
    show,
    onClose,
    employeeId,
    existingForemanIds = [],
    preferredForeman = null,
    onSaveCallback,
}) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isValidated, setIsValidated] = useState(false);

    // The form stays mounted while closed, so clear the last attempt's validation and error then.
    useEffect(() => {
        if (!show) {
            setIsValidated(false);
            setError(null);
        }
    }, [show]);

    const existing = preferredForeman;
    const canRemove = useInstanceSelector(checkUserHasPermission, ApplicationPermissions.employee_edit);

    // The row being edited keeps its own foreman selectable; every other pairing the employee
    // already has stays off the list.
    const unavailableForemanIds = existingForemanIds.filter((id) => id !== existing?.locationContactId);

    const { data: companies, isLoading: companiesLoading } = useQuery({
        queryKey: ['customers'],
        queryFn: () => httpClient.js(ApiRoutes.typeAheads.customers()),
        enabled: show,
    });

    const onSubmit = useCallback(async ({ foremanId }) => {
        setSaving(true);
        setError(null);

        try {
            if (existing) {
                await httpClient.put(ApiRoutes.employeePreferredForeman.update(existing.id), {
                    locationContactId: foremanId,
                });
                toasty.success('Preferred Foreman Updated');
            } else {
                await httpClient.post(ApiRoutes.employeePreferredForeman.create(), {
                    employeeId,
                    locationContactId: foremanId,
                });
                toasty.success('Preferred Foreman Added');
            }

            await onSaveCallback();
        } catch (e) {
            setError('This preferred foreman could not be saved.');
            toasty.error('Save Unsuccessful', e?.message);
        } finally {
            setSaving(false);
        }
    }, [existing, employeeId, onSaveCallback]);

    const onDelete = useCallback(async () => {
        setSaving(true);
        setError(null);

        try {
            await httpClient.delete(ApiRoutes.employeePreferredForeman.delete(existing.id));
            toasty.success('Preferred Foreman Removed');
            await onSaveCallback();
        } catch (e) {
            setError('This preferred foreman could not be removed.');
            toasty.error('Remove Unsuccessful', e?.message);
        } finally {
            setSaving(false);
        }
    }, [existing, onSaveCallback]);

    const initialValues = existing
        ? { companyId: existing.companyId, foremanId: existing.locationContactId }
        : { companyId: null, foremanId: null };

    return (
        <SlideForm
            show={show}
            id="preferredForemanForm"
            formIcon={faHardHat}
            formTitle={existing ? 'Edit Preferred Foreman' : 'Add Preferred Foreman'}
            onSubmit={onSubmit}
            onClose={onClose}
            onDelete={existing && canRemove ? onDelete : undefined}
            entityId={existing && canRemove ? existing.id : undefined}
            saving={saving}
            error={error}
            isValidated={isValidated}
            setIsValidated={setIsValidated}
            initialValues={initialValues}
        >
            {({ setFieldValue }) => (
                <>
                    <ValidatedSelect
                        id="companyId"
                        name="companyId"
                        label="Company"
                        required
                        options={companies}
                        isLoading={companiesLoading}
                        onChange={() => setFieldValue('foremanId', null)}
                        error="A company selection is required."
                    />

                    <ForemanSelect unavailableForemanIds={unavailableForemanIds} />
                </>
            )}
        </SlideForm>
    );
};

export default EmployeePreferredForemanForm;
